import { DashboardTopicValue } from '../types';
import { ResolvedSlotStatus, resolveLiveStatusesFromMqtt } from './resolveLiveStatus';
import { SignalsDefinition, signalsBaseTopic, signalsMqttTopics } from './types';

/**
 * Everything a placed stacklight needs to read live state and send commands.
 * Dashboard widgets and shopfloor entries both reduce to this; neither page may
 * derive topics, live status, or command targets on its own.
 */
export interface StacklightBinding {
  /** Definition (signal file id). One definition may run on several instances. */
  signalName?: string;
  /** Instance id on the controller (`devices[].id` / Flow `of`). */
  stacklight?: string;
  hostname?: string;
  /** MQTT base for `{base}/{Slot}`; from the catalog widget (flow edge or hostname). */
  topic?: string;
}

interface CatalogLike extends StacklightBinding {
  id?: string;
  type?: string;
}

/** Instance topic first: a definition shared by two stacklights has no single base. */
export function stacklightBaseTopic(
  binding: StacklightBinding | null | undefined,
  def: SignalsDefinition | null | undefined,
): string {
  return signalsBaseTopic(binding?.topic || def);
}

export function stacklightWatchTopics(
  binding: StacklightBinding | null | undefined,
  def: SignalsDefinition | null | undefined,
): string[] {
  const base = stacklightBaseTopic(binding, def);
  if (!base) return [];
  if (def) return signalsMqttTopics({ ...def, mqttTopic: base });
  return [base, `${base}/effect`, `${base}/color`];
}

export function resolveStacklightLive(
  binding: StacklightBinding | null | undefined,
  def: SignalsDefinition | null | undefined,
  values: Record<string, DashboardTopicValue | null | undefined> | null | undefined,
): ResolvedSlotStatus[] {
  return resolveLiveStatusesFromMqtt(def, values, {
    preferPreviewWhenEmpty: true,
    baseTopic: stacklightBaseTopic(binding, def),
  });
}

/** Props for `SignalsCommandDialog`; the server picks the instance from stacklight + hostname. */
export function stacklightCommandTarget(
  binding: StacklightBinding | null | undefined,
  def: SignalsDefinition | null | undefined,
): { signalName: string; stacklight?: string; hostname?: string; baseTopic: string } {
  return {
    signalName: binding?.signalName || def?.metadata?.name || '',
    stacklight: binding?.stacklight || undefined,
    hostname: binding?.hostname || undefined,
    baseTopic: stacklightBaseTopic(binding, def),
  };
}

/**
 * Fill missing binding fields from the live catalog. Saved layouts predate
 * fields like `hostname`/`stacklight`; the catalog (built from flows) is current.
 */
export function bindStacklightFromCatalog<T extends StacklightBinding & { catalogId?: string }>(
  item: T,
  catalog: readonly CatalogLike[],
): T {
  const stacklights = catalog.filter((c) => c.type === 'stacklight');
  const hit = (item.catalogId && stacklights.find((c) => c.id === item.catalogId))
    || (item.stacklight && item.hostname && stacklights.find(
      (c) => c.stacklight === item.stacklight && c.hostname === item.hostname,
    ))
    || (item.signalName && (() => {
      const same = stacklights.filter((c) => c.signalName === item.signalName);
      return same.length === 1 ? same[0] : undefined;
    })());
  if (!hit) return item;
  return {
    ...item,
    signalName: item.signalName || hit.signalName,
    stacklight: item.stacklight || hit.stacklight,
    hostname: item.hostname || hit.hostname,
    topic: item.topic || hit.topic,
  };
}
